/**
 * Stundenplan RTK Query — Query endpoints.
 *
 * Split from stundenplanApi.ts for readability.
 * Contains: getSchedulePeriods, getStundenplanStaticBundle, getStundenplanWeekBundle.
 *
 * @module klassenbuch/stundenplan/api/queries
 */

import { rootApi } from "../../../../lib/api/rootApi";
import {
  getSchedulePeriods,
  type SchedulePeriod,
} from "../../shared/data/klassenbuchDataAdapter";

import type {
  StundenplanStaticBundle,
  StundenplanWeekBundle,
} from "./stundenplanApiTypes";

export const stundenplanQueryApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get schedule periods for the school.
     * Wraps: `getSchedulePeriods(schoolId, blockTypes)`
     */
    getSchedulePeriods: builder.query<
      SchedulePeriod[],
      { schoolId: string; blockTypes?: string[] }
    >({
      queryFn: async (args) => {
        try {
          const data = await getSchedulePeriods(args.schoolId, args.blockTypes);
          return { data };
        } catch (error) {
          return {
            error: {
              message:
                (error as Error).message ?? "Failed to fetch schedule periods",
              details: "",
              hint: "",
              code: "ADAPTER_ERROR",
            },
          };
        }
      },
      providesTags: [{ type: "Stundenplan", id: "PERIODS" }],
    }),

    /**
     * Static data bundle — called ONCE on page mount.
     * Returns: schedule_periods, school_days, available_classes,
     *          staff_class_links, teacher_options
     */
    getStundenplanStaticBundle: builder.query<
      StundenplanStaticBundle,
      { entityType?: string }
    >({
      query: ({ entityType }) => ({
        type: "rpc" as const,
        rpc: "fn_stundenplan_get_static_bundle",
        params: {
          p_entity_type: entityType || "class",
        },
      }),
      transformResponse: (response: unknown): StundenplanStaticBundle => {
        const res = response as Record<string, unknown> | null;
        return {
          schedule_periods:
            (res?.schedule_periods as StundenplanStaticBundle["schedule_periods"]) ||
            [],
          school_days:
            (res?.school_days as StundenplanStaticBundle["school_days"]) || [],
          available_classes:
            (res?.available_classes as StundenplanStaticBundle["available_classes"]) ||
            [],
          staff_class_links:
            (res?.staff_class_links as StundenplanStaticBundle["staff_class_links"]) ||
            [],
          teacher_options:
            (res?.teacher_options as StundenplanStaticBundle["teacher_options"]) ||
            [],
          room_options:
            (res?.room_options as StundenplanStaticBundle["room_options"]) ||
            [],
          aufsichten:
            (res?.aufsichten as StundenplanStaticBundle["aufsichten"]) ||
            undefined,
        };
      },
      providesTags: [{ type: "Stundenplan", id: "STATIC" }],
    }),

    /**
     * Weekly data bundle — called on every date/week change.
     * Returns: lessons, calendar_exceptions, room_options,
     *          class_absences, trip_counts
     */
    getStundenplanWeekBundle: builder.query<
      StundenplanWeekBundle,
      {
        classOrEntityId: string;
        entityType: "class" | "teacher" | "room" | "course";
        weekStart: string;
      }
    >({
      query: ({ classOrEntityId, entityType, weekStart }) => {
        // Strip prefix (e.g. "teacher-id:UUID") — RPC expects raw UUID
        const rawId = classOrEntityId.includes(":")
          ? classOrEntityId.split(":")[1]
          : classOrEntityId;
        return {
          type: "rpc" as const,
          rpc: "fn_stundenplan_get_week_bundle",
          params: {
            p_class_or_entity_id: rawId,
            p_entity_type: entityType,
            p_week_start: weekStart,
          },
        };
      },
      transformResponse: (response: unknown): StundenplanWeekBundle => {
        const res = response as Record<string, unknown> | null;
        return {
          lessons: (res?.lessons as StundenplanWeekBundle["lessons"]) || [],
          calendar_exceptions:
            (res?.calendar_exceptions as StundenplanWeekBundle["calendar_exceptions"]) ||
            [],
          room_options:
            (res?.room_options as StundenplanWeekBundle["room_options"]) || [],
          class_absences:
            (res?.class_absences as StundenplanWeekBundle["class_absences"]) ||
            [],
          trip_counts:
            (res?.trip_counts as StundenplanWeekBundle["trip_counts"]) || {},
          attendance_access:
            (res?.attendance_access as StundenplanWeekBundle["attendance_access"]) ||
            {},
          aufsicht_substitutions:
            (res?.aufsicht_substitutions as StundenplanWeekBundle["aufsicht_substitutions"]) ||
            undefined,
          baende_assignments:
            (res?.baende_assignments as StundenplanWeekBundle["baende_assignments"]) ||
            undefined,
          baende_logs:
            (res?.baende_logs as StundenplanWeekBundle["baende_logs"]) ||
            undefined,
        };
      },
      providesTags: (_result, _error, args) => [
        {
          type: "Stundenplan",
          id: `WEEK_${args.classOrEntityId}_${args.weekStart}`,
        },
        { type: "Stundenplan", id: "WEEK" },
      ],
    }),
  }),
});

export const {
  useGetSchedulePeriodsQuery,
  useGetStundenplanStaticBundleQuery,
  useGetStundenplanWeekBundleQuery,
} = stundenplanQueryApi;
